import { ChartValueFormat, formatChartDataLabelValue } from './chartFormat'

type ChartRow = Record<string, unknown>

type BarRect = {
  x?: number
  y?: number
  width?: number
  height?: number
}

type FormattedBarItem = {
  item?: { type?: { displayName?: string } }
  props?: { data?: BarRect[] }
}

const sumSeriesOnRow = (
  row: ChartRow | undefined,
  seriesDataKeys: readonly string[],
): number => seriesDataKeys.reduce((acc, k) => acc + (Number(row?.[k]) || 0), 0)

const isBarItem = (gi: FormattedBarItem): boolean =>
  gi.item?.type?.displayName === 'Bar'

export interface GroupedVerticalBarTotalsLayerProps {
  formattedGraphicalItems?: FormattedBarItem[]
  rows: readonly ChartRow[]
  seriesDataKeys: readonly string[]
  valueFormat?: ChartValueFormat
  dataLabelFill?: string
}

/**
 * Draws one total label per category, horizontally centered on the full bar group
 * (uses Recharts layout from `formattedGraphicalItems`, not a single bar’s center).
 */
export const GroupedVerticalBarTotalsLayer = ({
  formattedGraphicalItems,
  rows,
  seriesDataKeys,
  valueFormat,
  dataLabelFill = 'white',
}: GroupedVerticalBarTotalsLayerProps) => {
  const barItems =
    formattedGraphicalItems
      ?.filter(isBarItem)
      .filter(
        gi => Array.isArray(gi.props?.data) && gi.props!.data!.length > 0,
      ) ?? []

  if (!barItems.length || !rows.length || !seriesDataKeys.length) return null

  const textStyle = {
    fill: dataLabelFill,
    fontSize: 12,
    fontWeight: 700 as const,
  }

  return (
    <g className="recharts-grouped-bar-totals">
      {rows.map((row, catIndex) => {
        const lefts: number[] = []
        const rights: number[] = []
        const tops: number[] = []

        for (const gi of barItems) {
          const cell = gi.props?.data?.[catIndex]
          if (
            cell &&
            typeof cell.x === 'number' &&
            typeof cell.width === 'number' &&
            typeof cell.y === 'number'
          ) {
            lefts.push(cell.x)
            rights.push(cell.x + cell.width)
            tops.push(cell.y)
          }
        }

        if (!lefts.length || !tops.length) return null

        const centerX = (Math.min(...lefts) + Math.max(...rights)) / 2
        const topY = Math.min(...tops)
        const total = sumSeriesOnRow(row, seriesDataKeys)

        return (
          <text
            key={catIndex}
            x={centerX}
            y={topY - 8}
            textAnchor="middle"
            {...textStyle}
          >
            {formatChartDataLabelValue(total, valueFormat)}
          </text>
        )
      })}
    </g>
  )
}
